import { useEffect, useRef, useState, useMemo } from "react";
import { toPng } from "html-to-image";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { useAtom } from "jotai";
import { Document, Page, View, Image, PDFViewer, Text } from "@react-pdf/renderer";
import { growthBySectorIndustryFilterAtom, growthBySectorMainDataAtom, growthBySectorOtherDataAtom, displayMunicNameAtom, growthBySectorDataAtom, selectedPlaceAtom, imageResponseAtom, selectedSpatialLayerAtom } from "../../shared/state/atoms";
import { Box, CircularProgress, Typography } from "@mui/material";
import styles from "../../pdfs/styles/PdfStyles";
import { useWindowDimensions } from "../../shared/hooks";

/**
 * Growth By Sector PDF Component
 *
 * This component renders the Growth By Sector dashboard as a PDF.
 * It includes multiple charts across multiple pages showing growth by different sectors.
 *
 * IMPORTANT NOTES ON PDF GENERATION:
 * - The component uses dynamic batching to handle variable numbers of industry charts
 * - Memory management through sequential processing and resource cleanup
 * - Each batch processes a single page to minimize memory pressure
 * - Implements advanced error handling and fallback mechanisms
 * - See /docs/PDF_GENERATION.md for more details on the PDF generation process
 */
function GrowthBySectorPdf() {
  const [displayMunicname] = useAtom(displayMunicNameAtom);
  const [industryFilter] = useAtom(growthBySectorIndustryFilterAtom);
  const [mainData] = useAtom(growthBySectorMainDataAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [imageResponse] = useAtom(imageResponseAtom);
  const [otherData] = useAtom(growthBySectorOtherDataAtom);
  const [data] = useAtom(growthBySectorDataAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  
  // State for page image URLs - support up to 5 pages dynamically
  const [pageImages, setPageImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingStatus, setProcessingStatus] = useState('Initializing...');
  const [progress, setProgress] = useState(0);
  
  // Refs for up to 5 pages (first page contains main chart + 3 small charts)
  const pageRef0 = useRef();
  const pageRef1 = useRef();
  const pageRef2 = useRef();
  const pageRef3 = useRef();
  const pageRef4 = useRef();
  const pageRefs = useMemo(() => [pageRef0, pageRef1, pageRef2, pageRef3, pageRef4], [pageRef0, pageRef1, pageRef2, pageRef3, pageRef4]);
  
  const { height, width } = useWindowDimensions();
  const COLORS = ["#F0A400", "#009641"];

  // Calculate how many pages we'll need based on the data
  const firstPageCapacity = 3; // Main chart + 3 small charts on first page
  const additionalPageCapacity = 9; // 9 small charts on each additional page
  
  // Calculate total industries and pages needed
  const totalIndustries = otherData ? otherData.length : 0;
  const remainingIndustries = Math.max(0, totalIndustries - firstPageCapacity);
  const additionalPagesNeeded = Math.ceil(remainingIndustries / additionalPageCapacity);
  const totalPages = 1 + Math.min(4, additionalPagesNeeded); // Limit to 5 pages maximum (1 + 4)
  
  // Console log for debugging
  console.log(`Growth by Sector: Total industries: ${totalIndustries}, Pages needed: ${totalPages}`);

  useEffect(() => {
    // Helper function to create fallback image when conversion fails
    const createFallbackImage = (pageIndex) => {
      try {
        console.log(`Creating fallback image for Growth by Sector page ${pageIndex + 1}`);
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 600;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#f8f9fa';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = '16px Arial';
        ctx.fillStyle = '#212529';
        ctx.textAlign = 'center';
        ctx.fillText('Chart visualization temporarily unavailable in PDF', canvas.width/2, canvas.height/2);

        return canvas.toDataURL('image/png');
      } catch (err) {
        console.error(`Error creating fallback image for page ${pageIndex + 1}:`, err);
        // Return a base64 string for a 1x1 transparent PNG as absolute fallback
        return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      }
    };

    // Process a single page with memory cleanup
    const processPage = async (pageIndex) => {
      setProcessingStatus(`Processing page ${pageIndex + 1} of ${totalPages}...`);
      setProgress(Math.round((pageIndex / totalPages) * 100));
      
      try {
        // Skip if node reference is invalid
        if (!pageRefs[pageIndex] || !pageRefs[pageIndex].current) {
          console.warn(`Page ${pageIndex + 1} ref is not available`);
          return createFallbackImage(pageIndex);
        }

        console.log(`Capturing Growth by Sector page ${pageIndex + 1}`);

        // Enhanced options for better chart capture
        const options = {
          cacheBust: true,
          quality: 1.0,
          pixelRatio: window.devicePixelRatio,
          // Properly handle loading errors with specific resources
          onCloneNode: (node) => {
            try {
              // Handle SVG elements from Recharts
              if (node.tagName === 'svg' || 
                  (node.tagName === 'STYLE' && 
                  node.textContent && 
                  node.textContent.includes('recharts'))) {
                const clonedNode = node.cloneNode(true);
                return clonedNode;
              }
              return node;
            } catch (err) {
              console.warn(`Error in onCloneNode for page ${pageIndex + 1}:`, err);
              return node;
            }
          },
          // Include all nodes for Recharts
          filter: (node) => true,
          // Custom fetch for resources
          fetchRequestInit: {
            mode: 'cors',
            credentials: 'same-origin'
          }
        };

        // Convert the page to an image with a timeout
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error(`Timeout converting page ${pageIndex + 1}`)), 25000)
        );
        
        const conversionPromise = toPng(pageRefs[pageIndex].current, options);
        
        // Race between conversion and timeout
        const dataUrl = await Promise.race([conversionPromise, timeoutPromise]);
        
        console.log(`Growth by Sector page ${pageIndex + 1} capture successful`);
        
        // Clean up memory
        if (pageRefs[pageIndex].current) {
          // Clear any cached data in the ref that's no longer needed
          const element = pageRefs[pageIndex].current;
          if (element._cachedImage) element._cachedImage = null;
          if (element._chartInstance) element._chartInstance = null;
          if (element._cachedData) element._cachedData = null;
          
          // Attempt to help GC by nullifying properties
          const svgElements = element.querySelectorAll('svg');
          svgElements.forEach(svg => {
            if (svg._reactEvents) svg._reactEvents = null;
          });
        }
        
        // Suggest garbage collection between pages
        if (window.gc) window.gc();
        
        return dataUrl;
      } catch (err) {
        console.error(`Failed to convert Growth by Sector page ${pageIndex + 1} to image:`, err);
        return createFallbackImage(pageIndex);
      }
    };
    
    // Process all pages sequentially
    const processAllPages = async () => {
      const newPageImages = [];
      
      // Process each page sequentially to reduce memory pressure
      for (let i = 0; i < totalPages; i++) {
        try {
          // Process the current page
          const imageUrl = await processPage(i);
          newPageImages.push(imageUrl);
          
          // Small delay between pages to allow memory cleanup
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`Error processing page ${i + 1}:`, error);
          newPageImages.push(createFallbackImage(i));
        }
      }
      
      // Update state with all page images
      setPageImages(newPageImages);
      setProcessingStatus('Complete');
      setProgress(100);
      setIsLoading(false);
    };
    
    // Wait a moment for the component to render before starting processing
    const timer = setTimeout(() => {
      processAllPages();
    }, 1000);
    
    // Fallback timer in case processing gets stuck
    const fallbackTimer = setTimeout(() => {
      if (isLoading) {
        console.log("Fallback: Growth by Sector pages didn't complete in time");
        
        // Create fallback images for any missing pages
        const fallbackImages = [];
        for (let i = 0; i < totalPages; i++) {
          fallbackImages.push(createFallbackImage(i));
        }
        
        setPageImages(fallbackImages);
        setProcessingStatus('Fallback Complete');
        setProgress(100);
        setIsLoading(false);
      }
    }, 90000); // Extended timeout for complex multi-page rendering
    
    return () => {
      console.log("GrowthBySectorPdf component unmounting - cleaning up");
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
      
      // Clear page images to free memory
      setPageImages([]);
      setProcessingStatus('Cleaned up');
      setProgress(0);
      
      // Force garbage collection hint
      if (window.gc) {
        window.gc();
      }
    };
  }, [totalPages, isLoading, pageRefs]);

  // Component for small charts with optimized rendering and consistent grid spacing
  const SmallChartComponent = ({ dataSlice }) => {
    // Ensure we always have placeholder slots for a 3-column grid, even if fewer charts
    const gridSize = 3; // 3-column grid
    const chartCount = dataSlice ? dataSlice.length : 0;

    // Create placeholders array to ensure consistent grid layout
    // If we have 1 or 2 charts, add empty placeholders to maintain 3-column grid spacing
    const placeholders = Array.from({ length: Math.max(0, gridSize - chartCount) });

    return (
      <div style={{ display: "flex", flexWrap: "wrap", marginLeft: 105, marginTop: 20 }}>
        {/* Render actual charts */}
        {dataSlice && dataSlice.map((chart, index) => (
          <div key={`chart-${index}`} style={{ marginRight: 20, marginBottom: 20, width: 400 }}>
            <div style={{ height: 90, width: "100%" }}>
              {chart?.graph_label?.length >= 62 ? chart?.graph_label.substring(0, 62) + " ..." : chart?.graph_label}
            </div>
            <ResponsiveContainer width={300} height={300}>
              <LineChart
                data={chart?.data}
                height={height * 0.4}
                width={(width - 400) * 0.3}
                margin={{
                  top: 10,
                  bottom: 10,
                }}
              >
                <XAxis dataKey="year" />
                <YAxis
                  domain={["auto", "auto"]}
                  label={{
                    value: data && data[0] ? data[0]?.y_axis_label : '',
                    angle: -90,
                    dx: -17,
                    fontSize: 12,
                  }}
                />
                <CartesianGrid strokeDasharray="3" vertical={false} />
                <Line type="monotone" dataKey={`${selectedPlace?.name}`} stroke="#F0A400" isAnimationActive={false} />
                <Line type="monotone" dataKey="South Africa" stroke="#009640" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ marginLeft: 70, height: 95 }}>
              {chart?.data && chart.data.length > 0 && (
                <>
                  <p style={{ margin: 0, fontSize: 14, textDecoration: "underline", marginLeft: 170 }}>
                    {chart.data[chart.data.length - 1].year}
                  </p>
                  {Object.entries(chart.data[chart.data.length - 1])
                    .filter(([key]) => key !== "year" && !key.includes("_abs"))
                    .map(([key, value], idx) => {
                      // Determine correct color based on key name
                      const colorIndex = key === selectedPlace?.name ? 0 : key === "South Africa" ? 1 : idx % COLORS.length;
                      const color = COLORS[colorIndex];
                      
                      return (
                        <div key={`legend-${idx}`} style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: 200 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div style={{ width: 10, height: 10, marginRight: 10, backgroundColor: color }} />
                            <p style={{ margin: 0, width: 150 }}>{key}</p>
                          </div>
                          <p style={{ margin: 0, width: 50 }}>{value}</p>
                        </div>
                      );
                  })}
                </>
              )}
            </div>
          </div>
        ))}

        {/* Add empty placeholder divs to maintain grid layout */}
        {placeholders.map((_, index) => (
          <div key={`placeholder-${index}`} style={{ marginRight: 20, marginBottom: 20, width: 400 }}>
            {/* Empty placeholder to maintain grid structure */}
          </div>
        ))}
      </div>
    );
  };

  // Get data slice for a specific page
  const getPageContent = (pageIndex) => {
    if (pageIndex === 0) {
      // First page has up to 3 small charts (after main chart)
      return otherData ? otherData.slice(0, firstPageCapacity) : [];
    } else {
      // Subsequent pages have 9 small charts each
      const startIdx = firstPageCapacity + (pageIndex - 1) * additionalPageCapacity;
      const endIdx = startIdx + additionalPageCapacity;
      return otherData ? otherData.slice(startIdx, endIdx) : [];
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
          <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
            <CircularProgress color="success" size="3rem" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Generating Growth by Sector PDF
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {processingStatus} ({progress}%)
            </Typography>
          </Box>
        </div>
      ) : pageImages.length > 0 ? (
        <PDFViewer style={{ flex: 1, width: "100%", height: "100vh" }}>
          <Document>
            {/* Dynamically render pages based on the images we've generated */}
            {pageImages.map((imageUrl, index) => (
              <Page key={`page-${index}`} size="A4" style={styles.page}>
                <Image style={styles.headerLogo} src={`data:image/png;base64,'${imageResponse[13].base64_image}'`}></Image>
                <Text style={styles.headerText}>
                  {selectedSpatialLayer?.name} | {displayMunicname}
                </Text>
                <View>
                  <Text style={{ ...styles.header, backgroundColor: "#FB8682" }}>Economic Growth</Text>
                </View>
                <View style={{ ...styles.section, paddingBottom: 0 }}>
                  <Text style={{ ...styles.heading, marginBottom: 0 }}>GROWTH BY SECTOR</Text>
                </View>
                <View style={{ ...styles.section, paddingBottom: 0 }}>
                  <Text style={styles.subheading}>
                    {industryFilter} | {index + 1}/{pageImages.length}
                  </Text>
                </View>
                {index === 0 && (
                  <View>
                    <Text style={styles.paragraph}>How does jobs growth compare between and within sectors in {displayMunicname}?</Text>
                    <Text style={styles.paragraph}>
                      Total jobs growth hides important differences in performance both between and within sectors of the local economy. 
                      National trends are provided as a useful benchmark in each case. Exploring which particular sub-sectors are leading 
                      or lagging in jobs growth is critical for policy makers in better supporting local industry. Jobs growth has been 
                      indexed to show performance relative to industry size in all figures. Very small industries with less than or equal 
                      to 200 employees have been omitted.
                    </Text>
                  </View>
                )}
                <View style={styles.section}>
                  <Image style={{ ...styles.image, marginTop: index === 0 ? 20 : 0, width: 550 }} src={imageUrl} />
                </View>
                <View style={styles.footerContainer}>
                  <Text style={styles.footer}>www.spatialtaxdata.org.za</Text>
                </View>
              </Page>
            ))}
          </Document>
        </PDFViewer>
      ) : null}
      
      {/* Hidden chart containers for conversion */}
      {/* Page 1 - Main chart + first 3 small charts */}
      <div style={{ position: "absolute", left: "-10000px" }}>
        <div ref={pageRefs[0]}>
          <div className="px-10 flex justify-center">
            {mainData?.graph_label?.length >= 62 ? mainData?.graph_label.substring(0, 62) + " ..." : mainData?.graph_label}
          </div>
          <ResponsiveContainer width={1400} height={500}>
            <LineChart
              data={mainData?.data}
              height={height * 0.45}
              width={width * 0.7}
              margin={{
                top: 10,
                right: 50,
                left: 100,
                bottom: 50,
              }}
              animationDuration={0} // Disable animation for faster rendering
            >
              <XAxis dataKey="year" />
              <YAxis
                domain={["auto", "auto"]}
                label={{
                  value: data && data[0] ? data[0]?.y_axis_label : '',
                  angle: -90,
                  dx: -50,
                }}
              />
              <CartesianGrid strokeDasharray="3" vertical={false} />
              <Line 
                type="monotone" 
                dataKey={`${selectedPlace?.name}`} 
                stroke="#f0a400" 
                strokeWidth={3}
                isAnimationActive={false} 
              />
              <Line 
                type="monotone" 
                dataKey="South Africa" 
                stroke="#009640" 
                strokeWidth={3}
                isAnimationActive={false} 
              />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ marginLeft: 150, marginBottom: 20 }}>
            {mainData?.data && mainData.data.length > 0 && (
              <>
                <p style={{ margin: 0, fontSize: 14, textDecoration: "underline", marginLeft: 250 }}>
                  {mainData.data[mainData.data.length - 1].year}
                </p>
                {Object.entries(mainData.data[mainData.data.length - 1])
                  .filter(([key]) => key !== "year" && !key.includes("_abs"))
                  .map(([key, value], index) => {
                    // Determine correct color based on key name
                    const colorIndex = key === selectedPlace?.name ? 0 : key === "South Africa" ? 1 : index % COLORS.length;
                    const color = COLORS[colorIndex];
                    
                    return (
                      <div key={`main-legend-${index}`} style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: 300 }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div style={{ width: 10, height: 10, marginRight: 10, backgroundColor: color }} />
                          <p style={{ margin: 0, width: 150 }}>{key}</p>
                        </div>
                        <p style={{ margin: 0, width: 50 }}>{value}</p>
                      </div>
                    );
                  })}
              </>
            )}
          </div>
          <SmallChartComponent dataSlice={getPageContent(0)} />
        </div>
      </div>
      
       {/* Generate additional page refs based on data volume */}
       {Array.from({ length: Math.min(4, additionalPagesNeeded) }).map((_, i) => {
         const pageIndex = i + 1;
         const pageContent = getPageContent(pageIndex);
         
         // Split into 3 rows of 3 charts each
         const firstRow = pageContent.slice(0, 3);
         const secondRow = pageContent.slice(3, 6);
         const thirdRow = pageContent.slice(6, 9);
         
         return (
           <div key={`page-container-${pageIndex}`} style={{ position: "absolute", left: "-10000px" }}>
             <div ref={pageRefs[pageIndex]}>
               {firstRow.length > 0 && <SmallChartComponent dataSlice={firstRow} />}
               {secondRow.length > 0 && <SmallChartComponent dataSlice={secondRow} />}
               {thirdRow.length > 0 && <SmallChartComponent dataSlice={thirdRow} />}
             </div>
           </div>
         );
       })}    </>
  );
}

export default GrowthBySectorPdf;